import React, { useState, useEffect } from 'react';
import { getLogs } from '../../services/adminApi';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../../components/ui/dialog';

const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isJsonDialogOpen, setIsJsonDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    entity_type: '',
    user_id: '',
    action: '',
    startDate: '',
    endDate: '',
  });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const response = await getLogs(filters);
        if (response.data.success) {
          setLogs(response.data.data);
          setTotalPages(response.data.totalPages);
        }
      } catch (error) {
        console.error('Failed to fetch logs', error);
      }
      setLoading(false);
    };
    fetchLogs();
  }, [filters]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  return (
    <AdminLayout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Admin Action Logs</h1>
        <div className="mb-4 grid grid-cols-1 md:grid-cols-5 gap-4">
          <select
            name="entity_type"
            value={filters.entity_type}
            onChange={handleFilterChange}
            className="p-2 border rounded"
          >
            <option value="">All Entities</option>
            <option value="Product">Product</option>
            <option value="Category">Category</option>
            <option value="Tag">Tag</option>
            <option value="SizeChart">Size Chart</option>
            <option value="Coupon">Coupon</option>
            <option value="GiftCard">Gift Card</option>
            <option value="Order">Order</option>
            <option value="Customer">Customer</option>
            <option value="MADCoins">MAD Coins</option>
            <option value="Settings">Settings</option>
            <option value="Email">Email</option>
          </select>
          <input
            type="text"
            name="user_id"
            placeholder="User ID"
            value={filters.user_id}
            onChange={handleFilterChange}
            className="p-2 border rounded"
          />
          <select
            name="action"
            value={filters.action}
            onChange={handleFilterChange}
            className="p-2 border rounded"
          >
            <option value="">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="status_change">Status Change</option>
          </select>
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
            className="p-2 border rounded"
          />
          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
            className="p-2 border rounded"
          />
        </div>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b">Timestamp</th>
                    <th className="py-2 px-4 border-b">Entity</th>
                    <th className="py-2 px-4 border-b">Action</th>
                    <th className="py-2 px-4 border-b">User</th>
                    <th className="py-2 px-4 border-b">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td className="py-2 px-4 border-b">{new Date(log.created_at).toLocaleString()}</td>
                      <td className="py-2 px-4 border-b">{log.entity_type} - {log.entity_name}</td>
                      <td className="py-2 px-4 border-b">{log.action}</td>
                      <td className="py-2 px-4 border-b">{log.user_name || log.user_id?.email}</td>
                      <td className="py-2 px-4 border-b">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedLog(log);
                            setIsJsonDialogOpen(true);
                          }}
                        >
                          View JSON
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-between items-center">
              <button
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page <= 1}
                className="p-2 border rounded"
              >
                Previous
              </button>
              <span>
                Page {filters.page} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page >= totalPages}
                className="p-2 border rounded"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* JSON Details Dialog */}
      <Dialog open={isJsonDialogOpen} onOpenChange={setIsJsonDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Log Details - {selectedLog?.entity_type} - {selectedLog?.action}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <pre className="bg-gray-100 p-4 rounded whitespace-pre-wrap text-sm overflow-auto">
              {selectedLog ? JSON.stringify(selectedLog.changes, null, 2) : ''}
            </pre>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default LogsPage;
